import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CountryFlag, GlobeInline, AvatarIcon } from "@/lib/identity";
import { PremiumBadge } from "@/components/PremiumBadge";
import { Crown, Trophy, ArrowLeft, MapPin, ChartLineUp } from "@phosphor-icons/react";

const REGION_LABEL = {
  West: "West Africa", East: "East Africa", Central: "Central Africa",
  North: "North Africa", Southern: "Southern Africa", ROW: "Rest of World",
};

export default function CountryPage() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setData(null);
    setErr(null);
    setHistory(null);
    api.get(`/countries/${code}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Country not found"));
    api.get(`/countries/${code}/score-history?days=30`)
      .then((r) => setHistory(r.data))
      .catch(() => setHistory({ series: [] }));
  }, [code]);

  if (err) {
    return (
      <div className="max-w-3xl mx-auto py-16 text-center space-y-4">
        <p className="text-nk-mute">{err}</p>
        <Link to="/leaderboard" className="text-nk-accent hover:underline">← Back to leaderboard</Link>
      </div>
    );
  }
  if (!data) return <div className="text-nk-mute text-sm py-12">Loading…</div>;

  const { country: c, stats, leaderboard, past_leaders, sponsors = [] } = data;
  const isRow = c.country_code === "ROW";
  const champion = leaderboard[0];

  return (
    <div className="space-y-8">
      {/* Hero band — uses .nk-country-band gradient */}
      <header className="nk-country-band rounded-sm p-6 sm:p-8 -mx-2 sm:mx-0" data-testid="country-page-hero">
        <Link to="/leaderboard" className="inline-flex items-center gap-1.5 text-xs text-nk-cream/60 hover:text-nk-cream mb-4">
          <ArrowLeft size={12} /> All countries
        </Link>
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="shrink-0">
            {isRow ? <GlobeInline size={56} /> : <CountryFlag country={c.country_code} size={72} />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="eyebrow text-nk-gold/80 mb-2 flex items-center gap-1.5">
              <MapPin size={11} weight="duotone" /> {REGION_LABEL[c.region] || c.region}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-nk-cream">
              {c.country_name}
            </h1>
            <p className="text-sm text-nk-cream/70 mt-3 max-w-xl">
              {c.playoff_eligible ? (
                <><span className="text-nk-gold">Playoff-active country</span> — championship + cash prizes at Day 30.</>
              ) : c.is_african ? (
                <><span className="text-nk-cream">Ranked country</span> — Country Leader title and Hall of Fame recognition at season end. Cash prizes unlock as the league grows.</>
              ) : (
                <>Rest of World — separate ranking track, no national championship.</>
              )}
            </p>
          </div>
          {c.playoff_eligible && (
            <span className="self-start px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider rounded-sm bg-nk-gold/20 text-nk-gold border border-nk-gold/40">
              Playoff-Active
            </span>
          )}
        </div>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Total players" value={stats.total_players.toLocaleString()} />
        <Stat label="On the board" value={stats.ranked_players.toLocaleString()} hint={`activity gate: 10 games`} />
        <Stat label="Country score" value={stats.country_score.toLocaleString()} />
      </div>

      {/* Score history sparkline */}
      {history && history.series.length > 0 && (
        <ScoreHistoryChart history={history} countryName={c.country_name} />
      )}

      {/* Current Country Leader */}
      {champion && (
        <section className="nk-card-gold p-5 sm:p-6 rounded-sm" data-testid="country-page-leader">
          <div className="flex items-center gap-4">
            <AvatarIcon id={champion.avatar} size={56} />
            <div className="flex-1 min-w-0">
              <p className="eyebrow text-nk-gold mb-1 flex items-center gap-1.5">
                <Crown size={11} weight="fill" /> Current Country Leader
              </p>
              <Link to={`/profile/${champion.user_id}`} className="font-display text-2xl font-medium hover:text-nk-gold transition-colors">
                {champion.username}
              </Link>
              <p className="text-xs text-nk-mute mt-1 font-mono">
                {(champion.season_score || 0).toLocaleString()} SS · {champion.knowledge_rating || 1000} Rating
              </p>
            </div>
            <span className="hidden sm:inline-block px-2.5 py-1 text-[10px] font-medium uppercase tracking-wider rounded-sm bg-nk-accent/15 text-nk-accent border border-nk-accent/30">
              #1
            </span>
          </div>
        </section>
      )}

      {/* Leaderboard */}
      <section>
        <h2 className="font-display text-xl font-medium mb-3">Season Top 20</h2>
        <div className="nk-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-nk-line">
              <tr>
                <Th>Rank</Th><Th>Player</Th><Th>Season Score</Th><Th>Rating</Th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-12 text-center text-nk-mute text-sm">
                  No players on the board yet — play 10 games (account ≥ 2 days) to qualify.
                </td></tr>
              ) : leaderboard.map((r) => {
                const top3 = r.rank <= 3;
                return (
                  <tr key={r.user_id} className={`border-b border-nk-line/40 ${top3 ? "bg-nk-accent/5" : ""}`}>
                    <td className={`px-4 py-2.5 font-mono ${top3 ? "text-nk-gold font-bold" : "text-nk-mute"}`}>
                      {top3 && "★ "}#{r.rank}
                    </td>
                    <td className="px-4 py-2.5">
                      <Link to={`/profile/${r.user_id}`} className="hover:text-nk-accent transition-colors flex items-center gap-2">
                        <AvatarIcon id={r.avatar} size={20} />
                        <span className="font-medium">{r.username || "Player"}</span>
                        {r.current_country_leader_country === r.country && (
                          <Crown size={12} weight="fill" className="text-nk-gold" />
                        )}
                        <PremiumBadge active={r.nk_plus_active} size="sm" />
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-nk-accent">{(r.season_score || 0).toLocaleString()}</td>
                    <td className="px-4 py-2.5 font-mono">{r.knowledge_rating || 1000}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Past Leaders */}
      {past_leaders.length > 0 && (
        <section data-testid="country-page-past-leaders">
          <h2 className="font-display text-xl font-medium mb-3 flex items-center gap-2">
            <Trophy size={18} weight="duotone" className="text-nk-gold" />
            Past Country Leaders
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {past_leaders.map((d, i) => (
              <Link key={`${d.season_number}-${d.user_id}-${i}`} to={`/profile/${d.user_id}`}
                    className="nk-card p-3 flex items-center gap-3 hover:border-nk-gold/40 transition-colors">
                <AvatarIcon id={d.avatar} size={32} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{d.username}</p>
                  <p className="text-[10px] text-nk-dim font-mono">
                    Season {d.season_number} · {(d.season_score || 0).toLocaleString()} SS · {d.level === "national" ? "National Champion" : "Country Leader"}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Sponsors for this country */}
      {sponsors.length > 0 && (
        <section data-testid="country-page-sponsors">
          <h2 className="font-display text-xl font-medium mb-3 flex items-center gap-2">
            <span className="eyebrow text-nk-gold">Sponsors active in {c.country_name}</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sponsors.map((s) => (
              <SponsorCard key={s.sponsor_id} sponsor={s} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function SponsorCard({ sponsor }) {
  const inner = (
    <article className="nk-card-gold p-4 flex items-center gap-4 group" data-testid={`sponsor-card-${sponsor.sponsor_id}`}>
      {sponsor.logo_url
        ? <img src={sponsor.logo_url} alt={sponsor.name}
               className="w-12 h-12 rounded-sm object-contain bg-white/5 shrink-0" />
        : <div className="w-12 h-12 rounded-sm bg-nk-gold/10 border border-nk-gold/30 flex items-center justify-center shrink-0">
            <span className="text-nk-gold font-display text-lg">{sponsor.name?.[0] || "S"}</span>
          </div>}
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate group-hover:text-nk-gold transition-colors">{sponsor.name}</p>
        {sponsor.description && <p className="text-xs text-nk-mute mt-0.5 line-clamp-2">{sponsor.description}</p>}
      </div>
      <span className="text-[10px] text-nk-gold font-medium uppercase tracking-wider shrink-0">Sponsor</span>
    </article>
  );
  return sponsor.cta_url
    ? <a href={sponsor.cta_url} target="_blank" rel="noreferrer noopener">{inner}</a>
    : inner;
}

function Stat({ label, value, hint }) {
  return (
    <div className="nk-card p-4">
      <p className="text-[10px] uppercase tracking-wider text-nk-mute mb-1.5">{label}</p>
      <p className="font-display text-2xl text-nk-text">{value}</p>
      {hint && <p className="text-[10px] text-nk-dim mt-1">{hint}</p>}
    </div>
  );
}

function ScoreHistoryChart({ history, countryName }) {
  const w = 720, h = 140, pad = 10;
  const series = history.series || [];
  const peak = Math.max(1, history.peak_day_score || 1);
  const barW = (w - pad * 2) / series.length;
  const path = series.map((d, i) => {
    const x = pad + i * barW + barW / 2;
    const y = h - pad - ((d.score_added / peak) * (h - pad * 2));
    return `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(" ");

  return (
    <section data-testid="country-page-history">
      <h2 className="font-display text-xl font-medium mb-3 flex items-center gap-2">
        <ChartLineUp size={18} weight="duotone" className="text-nk-gold" />
        Country momentum · last 30 days
      </h2>
      <div className="nk-card p-4">
        <div className="grid grid-cols-3 gap-3 mb-4">
          <Stat label="SS earned" value={history.total_in_window.toLocaleString()} />
          <Stat label="Peak day" value={history.peak_day_score.toLocaleString()} />
          <Stat label="Active days" value={series.length} />
        </div>
        <svg viewBox={`0 0 ${w} ${h}`} className="w-full rounded-sm" style={{ background: "rgba(11,61,46,0.10)" }}>
          <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="#2C3E36" strokeWidth="0.5" />
          {series.map((d, i) => {
            const barH = (d.score_added / peak) * (h - pad * 2);
            const x = pad + i * barW + 1;
            return (
              <rect key={d.date} x={x} y={h - pad - barH}
                    width={Math.max(1, barW - 2)} height={barH}
                    fill="#D4AF37" opacity="0.5" />
            );
          })}
          <path d={path} stroke="#D4AF37" strokeWidth="1.5" fill="none" />
        </svg>
        <p className="text-[10px] text-nk-dim mt-2 font-mono">
          {series[0]?.date} → {series[series.length - 1]?.date} · {countryName}
        </p>
      </div>
    </section>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 text-left text-xs text-nk-mute font-medium uppercase tracking-wider">{children}</th>;
}
